
import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { GraphNode, GraphLink, AtlasEntry, AtlasCategory } from '../types';
import { Plus, Minus, Maximize, Crosshair, Scan, Cpu, ShieldAlert, Shield, Brain, Scale, Circle, FileText } from 'lucide-react';

interface NeuralMapProps {
  onNodeClick: (nodeId: string) => void;
  width?: number;
  height?: number;
  entries: AtlasEntry[];
}

// Extend GraphNode to include category for shape rendering
interface ExtendedGraphNode extends GraphNode {
  category: AtlasCategory;
  severity?: string;
  fx?: number | null;
  fy?: number | null;
}

const NeuralMap: React.FC<NeuralMapProps> = ({ onNodeClick, width = 800, height = 400, entries }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [graphData, setGraphData] = useState<{ nodes: ExtendedGraphNode[]; links: GraphLink[] }>({ nodes: [], links: [] });
  const [hoveredNode, setHoveredNode] = useState<ExtendedGraphNode | null>(null);
  
  // Detect if we are in a filtered state (all entries share the same category)
  const activeCategory = entries.length > 0 && entries.every(e => e.category === entries[0].category) 
    ? entries[0].category 
    : null;

  // Ref to store the zoom behavior so we can call it programmatically
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  // Transform Data
  useEffect(() => {
    if (entries.length === 0) return;

    const nodes: ExtendedGraphNode[] = entries.map((entry) => ({
      id: entry.title,
      group: 1,
      val: entry.severity === 'Critical' || entry.severity === 'High' ? 35 : 25, // Slightly larger nodes
      label: entry.title,
      category: entry.category,
      severity: entry.severity
    }));

    const links: GraphLink[] = [];
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const entryA = entries[i];
        const entryB = entries[j];
        const sharedTags = entryA.tags.filter(tag => entryB.tags.includes(tag));
        const titleMatch = entryA.tags.includes(entryB.title.toLowerCase()) || entryB.tags.includes(entryA.title.toLowerCase());

        if (sharedTags.length > 0 || titleMatch) {
          links.push({
            source: entryA.title,
            target: entryB.title,
            value: sharedTags.length + (titleMatch ? 1 : 0)
          });
        }
      }
    }
    setGraphData({ nodes, links });
  }, [entries]);

  // Initialize D3
  useEffect(() => {
    if (!svgRef.current || graphData.nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove(); // Clean slate

    // Define Zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.2, 5])
        .on("zoom", (event) => {
            g.attr("transform", event.transform);
        });
    
    svg.call(zoom);
    zoomBehaviorRef.current = zoom;

    // Create container group
    const g = svg.append("g");

    // Center Initial View
    const initialTransform = d3.zoomIdentity.translate(width / 2, height / 2).scale(0.6);
    svg.call(zoom.transform, initialTransform);
    
    // --- FORCE SIMULATION CONFIG ---
    const chargeStrength = activeCategory ? -400 : -600; 
    const linkDistance = activeCategory ? 80 : 120; 

    const simulation = d3.forceSimulation(graphData.nodes)
      .force("link", d3.forceLink(graphData.links).id((d: any) => d.id).distance(linkDistance))
      .force("charge", d3.forceManyBody().strength(chargeStrength))
      .force("center", d3.forceCenter(0, 0))
      .force("collide", d3.forceCollide().radius((d: any) => (d.label.length * 4) + 20).strength(0.8));

    // Lookup for highlighting
    const linkedByIndex: Record<string, boolean> = {};
    graphData.links.forEach((d: any) => {
      linkedByIndex[`${d.source.id || d.source},${d.target.id || d.target}`] = true;
      linkedByIndex[`${d.target.id || d.target},${d.source.id || d.source}`] = true;
    });

    function isConnected(a: any, b: any) {
        return linkedByIndex[`${a.id},${b.id}`] || a.id === b.id;
    }

    // --- DRAW LINKS ---
    const link = g.append("g")
      .selectAll("line")
      .data(graphData.links)
      .join("line")
      .attr("stroke", "#e5e7eb")
      .attr("stroke-width", 1)
      .attr("class", "link");

    // --- DRAW NODES (DISTINCT GEOMETRY) ---
    const nodeGroup = g.append("g")
      .selectAll("g")
      .data(graphData.nodes)
      .join("g")
      .attr("cursor", "pointer")
      .call(d3.drag<any, any>()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended) as any)
      .on("click", (event, d) => onNodeClick(d.id));

    nodeGroup.each(function(d) {
        const el = d3.select(this);
        const sz = d.val;
        
        // --- 1. THREAT (MINACCIA) -> TRIANGLE ---
        if (d.category === AtlasCategory.THREAT) {
             const triangleSize = sz * 20; 
             el.append("path")
               .attr("d", d3.symbol().type(d3.symbolTriangle).size(triangleSize))
               .attr("fill", d.severity === 'Critical' ? "#ef4444" : "#ffffff") 
               .attr("stroke", "#dc2626") 
               .attr("stroke-width", 2);
             
             el.append("text")
               .text("!")
               .attr("dy", sz/3)
               .attr("text-anchor", "middle")
               .attr("font-family", "Arial")
               .attr("font-weight", "900")
               .attr("font-size", sz)
               .attr("fill", d.severity === 'Critical' ? "white" : "#dc2626");
        } 
        // --- 2. DEFENSE (DIFESA) -> SQUARE ---
        else if (d.category === AtlasCategory.DEFENSE) {
             const squareSize = sz * 2;
             el.append("rect")
                .attr("x", -squareSize/2).attr("y", -squareSize/2)
                .attr("width", squareSize).attr("height", squareSize)
                .attr("fill", "white")
                .attr("stroke", "#2563eb")
                .attr("stroke-width", 2);
             
             el.append("text")
               .text("+") 
               .attr("dy", sz/3)
               .attr("text-anchor", "middle")
               .attr("font-family", "Arial")
               .attr("font-weight", "bold")
               .attr("font-size", sz)
               .attr("fill", "#2563eb");
        }
        // --- 3. AI / MODELS -> HEXAGON (POLYGON) ---
        else if (d.category === AtlasCategory.AI_MODEL) {
             const hexSize = sz * 1.1;
             const points = [];
             for(let i=0; i<6; i++) {
                 const angle_deg = 60 * i - 30;
                 const angle_rad = Math.PI / 180 * angle_deg;
                 points.push([hexSize * Math.cos(angle_rad), hexSize * Math.sin(angle_rad)]);
             }
             const pointsStr = points.map(p => p.join(",")).join(" ");

             el.append("polygon")
               .attr("points", pointsStr)
               .attr("fill", "white")
               .attr("stroke", "#16a34a") 
               .attr("stroke-width", 2);
             
             el.append("circle").attr("r", 3).attr("fill", "#16a34a");
        }
        // --- 4. REGULATION (NORMATIVA) -> DIAMOND ---
        else if (d.category === AtlasCategory.REGULATION) {
             const diamondSize = sz * 25;
             el.append("path")
               .attr("d", d3.symbol().type(d3.symbolDiamond).size(diamondSize))
               .attr("fill", "white")
               .attr("stroke", "#9333ea") 
               .attr("stroke-width", 2);
               
             el.append("text")
               .text("§") 
               .attr("dy", sz/3)
               .attr("text-anchor", "middle")
               .attr("font-family", "serif")
               .attr("font-weight", "bold")
               .attr("font-size", sz)
               .attr("fill", "#9333ea");
        }
        // --- 5. CONCEPT / OTHER -> CIRCLE ---
        else {
             el.append("circle")
               .attr("r", sz)
               .attr("fill", "white")
               .attr("stroke", "#4b5563") 
               .attr("stroke-width", 2);
        }
    });

    // --- LABELS (PILL STYLE) ---
    const labels = nodeGroup.append("g")
        .attr("class", "label-group")
        .style("pointer-events", "none");

    labels.append("rect")
      .attr("rx", 4)
      .attr("ry", 4)
      .attr("fill", "#000000") 
      .attr("fill-opacity", 0.9)
      .attr("stroke", "none");

    labels.append("text")
      .text((d) => d.label)
      .attr("dx", 0)
      .attr("dy", (d) => d.val + 16) 
      .attr("text-anchor", "middle")
      .attr("fill", "#ffffff") 
      .attr("font-size", "10px")
      .attr("font-family", "Inter, sans-serif")
      .attr("font-weight", "600")
      .attr("letter-spacing", "0.5px")
      .style("text-transform", "uppercase");

    labels.each(function(d) {
        const textEl = d3.select(this).select("text").node() as SVGTextElement;
        if (textEl) {
            const bbox = textEl.getBBox();
            d3.select(this).select("rect")
                .attr("x", bbox.x - 6) 
                .attr("y", bbox.y - 2)
                .attr("width", bbox.width + 12)
                .attr("height", bbox.height + 4);
        }
    });

    // --- HOVER EFFECTS ---
    nodeGroup
      .on("mouseover", function(event, d: any) { 
        d3.select(this).raise();
        setHoveredNode(d);

        link.transition().duration(100)
            .style("stroke", (o: any) => (o.source.id === d.id || o.target.id === d.id) ? "#000" : "#eee")
            .style("stroke-width", (o: any) => (o.source.id === d.id || o.target.id === d.id) ? 2 : 1);
            
        nodeGroup.transition().duration(100).style("opacity", (o: any) => isConnected(d, o) ? 1 : 0.1);
      })
      .on("mouseout", function() { 
        setHoveredNode(null);
        nodeGroup.transition().duration(200).style("opacity", 1);
        link.transition().duration(200).style("stroke", "#e5e7eb").style("stroke-width", 1);
      });

    simulation.on("tick", () => {
      link
        .attr("x1", (d: any) => d.source.x)
        .attr("y1", (d: any) => d.source.y)
        .attr("x2", (d: any) => d.target.x)
        .attr("y2", (d: any) => d.target.y);

      nodeGroup.attr("transform", (d: any) => `translate(${d.x},${d.y})`);
    });

    function dragstarted(event: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event: any) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event: any) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    return () => {
      simulation.stop();
    };
  }, [graphData, width, height, onNodeClick, activeCategory]);

  const handleZoom = (scaleFactor: number) => {
      if (svgRef.current && zoomBehaviorRef.current) {
          d3.select(svgRef.current).transition().duration(500).call(zoomBehaviorRef.current.scaleBy, scaleFactor);
      }
  };
  const handleReset = () => {
      if (svgRef.current && zoomBehaviorRef.current) {
          d3.select(svgRef.current).transition().duration(750).call(
              zoomBehaviorRef.current.transform, 
              d3.zoomIdentity.translate(width / 2, height / 2).scale(0.6)
          );
      }
  };

  return (
    <div className="w-full bg-[#f8f9fa] border-y border-gray-200 relative group overflow-hidden select-none font-sans text-black">
      
      <div className="absolute inset-0 pointer-events-none opacity-20" 
           style={{ 
               backgroundImage: 'linear-gradient(#ccc 1px, transparent 1px), linear-gradient(90deg, #ccc 1px, transparent 1px)', 
               backgroundSize: '40px 40px' 
           }}>
      </div>
      
      {/* --- HUD OVERLAY (IMPROVED READABILITY with bg-black text-white) --- */}
      <div className="absolute top-4 left-4 z-10 pointer-events-none flex flex-col gap-3">
        <div className="bg-black text-white px-3 py-2 border border-white/20 shadow-xl flex items-center gap-3">
            <Scan size={16} className="text-green-400 animate-pulse" />
            <div>
                <div className="text-[10px] font-bold tracking-[0.2em] text-green-400 leading-none mb-1">
                    {activeCategory ? `FILTER: ${activeCategory.toUpperCase()} MODE` : 'LIVE SYSTEM'}
                </div>
                <div className="text-sm font-bold tracking-widest leading-none text-white">
                    {activeCategory ? 'SUBSET_VIEW' : 'NET_TOPOLOGY_V4'}
                </div>
            </div>
        </div>
        
        <div className="bg-white/90 backdrop-blur border border-black p-2 shadow-lg flex gap-4 text-[10px] font-bold text-black uppercase tracking-wider">
            <span className="flex items-center gap-1 text-red-600"><ShieldAlert size={12}/> THREATS: {graphData.nodes.filter(n => n.category === AtlasCategory.THREAT).length}</span>
            <span className="flex items-center gap-1 text-gray-600"><Cpu size={12}/> NODES: {graphData.nodes.length}</span>
        </div>
      </div>

      {/* 
          CRITICAL FIX FOR MOBILE: touch-action="none" 
          Questo impedisce che il tocco sulla mappa faccia scorrere l'intera pagina,
          permettendo il drag & drop dei nodi e il pan della mappa.
      */}
      <svg 
        ref={svgRef} 
        width={width} 
        height={height} 
        viewBox={`0 0 ${width} ${height}`} 
        className="w-full h-full relative z-0 cursor-move active:cursor-grabbing" 
        style={{ touchAction: 'none' }}
      />
      
      {/* --- LEGEND (IMPROVED ICONS) --- */}
      <div className="absolute bottom-6 left-6 z-10 bg-white/90 backdrop-blur text-black p-3 text-[10px] uppercase tracking-widest font-bold shadow-lg border border-gray-300 hidden md:block font-sans">
          <div className="mb-2 border-b border-gray-200 pb-1 text-gray-400">Classi Oggetto</div>
          <div className="flex items-center gap-3 mb-1.5"><ShieldAlert size={12} className="text-red-600"/> Minaccia (Triangolo)</div>
          <div className="flex items-center gap-3 mb-1.5"><Shield size={12} className="text-blue-600"/> Difesa (Quadrato)</div>
          <div className="flex items-center gap-3 mb-1.5"><Brain size={12} className="text-green-600"/> AI Model (Esagono)</div>
          <div className="flex items-center gap-3 mb-1.5"><Scale size={12} className="text-purple-600"/> Normativa (Rombo)</div>
          <div className="flex items-center gap-3"><Circle size={12} className="text-gray-500"/> Concetto (Cerchio)</div>
      </div>

      <div className="absolute bottom-6 right-6 flex flex-col gap-px bg-white border border-gray-300 shadow-lg p-1 z-20">
          <button onClick={() => handleZoom(1.3)} className="p-2 text-black hover:bg-gray-100 transition-colors" title="Zoom In">
              <Plus size={16} />
          </button>
          <button onClick={() => handleZoom(0.7)} className="p-2 text-black hover:bg-gray-100 transition-colors" title="Zoom Out">
              <Minus size={16} />
          </button>
          <div className="h-px bg-gray-200 mx-1"></div>
          <button onClick={handleReset} className="p-2 text-black hover:bg-gray-100 transition-colors" title="Reset View">
              <Maximize size={16} />
          </button>
      </div>
    </div>
  );
};

export default NeuralMap;
